import { prepareContent } from '@/lib/message';
import { forwardRef, memo } from 'react';

import type { IMessageElement, IStep } from '@chainlit/react-client';

import { CURSOR_PLACEHOLDER } from '@/components/BlinkingCursor';
import { Markdown } from '@/components/Markdown';
import type { SourceSection } from '@/components/SourceSummaryContext';
import { SourceSummaryProvider } from '@/components/SourceSummaryContext';

import { StreamingMarkdown } from './DMStreamingMarkdown';
import { InlinedElements } from './InlinedElements';

export interface Props {
    elements: IMessageElement[];
    message: IStep;
    allowHtml?: boolean;
    latex?: boolean;
}

const MessageContent = memo(
    forwardRef<HTMLDivElement, Props>(
        ({ message, elements, allowHtml, latex }, ref) => {
            const isStreaming = !!message.streaming;

            const outputContent = message.output;

            const {
                preparedContent: output,
                inlinedElements: outputInlinedElements,
                refElements: outputRefElements
            } = prepareContent({
                elements,
                id: message.id,
                content: outputContent,
                language: message.language
            });

            const displayInput = message.input && message.showInput;

            const isMessage = message.type.includes('message');

            const outputMarkdown = (
                <>
                    {!isMessage && displayInput && message.output ? (
                        <div className="font-medium">Output</div>
                    ) : null}
                    {isStreaming ? (
                        <StreamingMarkdown
                            targetContent={output}
                            streaming={isStreaming}
                            allowHtml={allowHtml}
                            latex={latex}
                            refElements={outputRefElements}
                        />
                    ) : (
                        <Markdown
                            allowHtml={allowHtml}
                            latex={latex}
                            refElements={outputRefElements}
                        >
                            {output}
                        </Markdown>
                    )}
                </>
            );

            let inputMarkdown;

            if (displayInput) {
                const inputContent = message.input;
                const {
                    preparedContent: input,
                    refElements: inputRefElements
                } = prepareContent({
                    elements,
                    id: message.id,
                    content: inputContent,
                    language:
                        typeof message.showInput === 'string'
                            ? message.showInput
                            : undefined
                });

                inputMarkdown = (
                    <>
                        <div className="font-medium">Input</div>
                        {isStreaming ? (
                            <StreamingMarkdown
                                targetContent={input}
                                streaming={isStreaming}
                                allowHtml={allowHtml}
                                latex={latex}
                                refElements={inputRefElements}
                            />
                        ) : (
                            <Markdown
                                allowHtml={allowHtml}
                                latex={latex}
                                refElements={inputRefElements}
                            >
                                {input}
                            </Markdown>
                        )}
                    </>
                );
            }

            const markdownContent = (
                <div className="flex flex-col gap-4">
                    {inputMarkdown}
                    {outputMarkdown}
                </div>
            );

            const sourcesEl = outputInlinedElements.find(
                (el) => el.name === 'Sources used' && el.type === 'custom'
            ) as any;
            const sources: SourceSection[] | null =
                sourcesEl?.props?.sources ?? null;

            // Filter out the data-only "Sources used" element (rendered via
            // SourceSummaryProvider instead) and "mcpApp" elements. mcpApp's
            // forId is the parent run step's id, which equals message.id when
            // this is the run step itself (CL_RUN_NAMES renders it invisibly
            // but its content still passes through here) — without this
            // filter it gets treated as a generic custom element and 404s
            // trying to load public/elements/mcpApp.jsx. The real rendering
            // (inline chart or click-to-open card) happens in Message/index.tsx
            // via appElements, keyed off the assistant message's parentId.
            const visibleElements = outputInlinedElements.filter(
                (el) =>
                    !(el.name === 'Sources used' && el.type === 'custom') &&
                    !(el.name === 'mcpApp' && el.type === 'custom')
            );

            return (
                <SourceSummaryProvider
                    sources={sources}
                    streaming={isStreaming}
                >
                    <div
                        ref={ref}
                        className="flex flex-col gap-2 w-full message-content"
                    >
                        {!!inputMarkdown || output ? markdownContent : null}
                        <InlinedElements elements={visibleElements} />
                    </div>
                </SourceSummaryProvider>
            );
        }
    )
);

export { MessageContent };
